import { useEffect } from 'react';

type SmsOtpAutofillOptions = {
  /** When false, cancel any in-flight WebOTP request. */
  enabled: boolean;
  onCode: (code: string) => void;
};

type OtpCredential = Credential & { code?: string };

/**
 * Chrome Android WebOTP: when the SMS includes `@<origin-host> #CODE`, the
 * browser can deliver the code into the focused one-time-code field.
 * No-ops on browsers without the API (iOS relies on autocomplete=one-time-code).
 */
export function useSmsOtpAutofill({ enabled, onCode }: SmsOtpAutofillOptions): void {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || typeof navigator === 'undefined') {
      return;
    }
    const credentials = navigator.credentials;
    const otpCapable =
      credentials &&
      typeof credentials.get === 'function' &&
      'OTPCredential' in window;
    if (!otpCapable) {
      return;
    }

    const ac = new AbortController();
    void (async () => {
      try {
        const cred = (await credentials.get({
          otp: { transport: ['sms'] },
          signal: ac.signal,
        } as CredentialRequestOptions)) as OtpCredential | null;
        const code = typeof cred?.code === 'string' ? cred.code.trim() : '';
        if (code) {
          onCode(code);
        }
      } catch {
        /* user dismissed / unsupported / aborted */
      }
    })();

    return () => {
      ac.abort();
    };
  }, [enabled, onCode]);
}
